import React, { useState, useEffect, useCallback } from 'react';
import { FiSearch, FiTruck, FiCheckCircle, FiRefreshCw, FiX, FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import API from '../utils/api';
import toast from 'react-hot-toast';
import { useStaffLog } from '../utils/useStaffLog';
import './SalesOrders.css';

/* ── helpers ── */
const safeArray = (v) => (Array.isArray(v) ? v : []);

const money = (n) =>
  `₹${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const fmtDate = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

/* ── Per-line qty helpers ── */
const totalQtyForLine = (entries, lineId) =>
  safeArray(entries)
    .filter((e) => e.itemLineId === lineId)
    .reduce((sum, e) => sum + (Number(e.qty) || 0), 0);

/* ── Dispatch progress across ALL items of an order ── */
const getDispatchProgress = (order) => {
  const items = safeArray(order.items);
  const deliveryEntries = safeArray(order.deliveryEntries);
  if (items.length === 0) return { dispatched: 0, total: 0, status: 'none' };

  let totalOrdered = 0;
  let totalDispatched = 0;

  items.forEach((line) => {
    const ordered = Number(line.qty) || 0;
    const dispatched = totalQtyForLine(deliveryEntries, line.id);
    totalOrdered += ordered;
    totalDispatched += Math.min(dispatched, ordered);
  });

  const status =
    totalDispatched === 0 ? 'pending'
    : totalDispatched >= totalOrdered ? 'done'
    : 'partial';

  return { dispatched: totalDispatched, total: totalOrdered, status };
};

/* ── Latest ready date across all items (last readyEntries date) ── */
const getLatestReadyDate = (order) => {
  const entries = safeArray(order.readyEntries);
  if (entries.length === 0) return null;
  return entries.reduce((latest, e) => {
    if (!e.date) return latest;
    return !latest || e.date > latest ? e.date : latest;
  }, null);
};

/* ── Payment status ── */
const paymentStatus = (order) => {
  if (order.paymentType === 'credit') {
    return { label: 'On Credit', color: '#059669', bg: '#d1fae5' };
  }
  const total    = Number(order.grandTotal)     || 0;
  const received = Number(order.amountReceived) || 0;
  if (total <= 0)        return { label: 'N/A',            color: '#6b7280', bg: '#f3f4f6' };
  if (received >= total) return { label: 'Fully Paid',     color: '#16a34a', bg: '#dcfce7' };
  if (received > 0)      return { label: 'Partially Paid', color: '#d97706', bg: '#fef3c7' };
  return                        { label: 'Unpaid',         color: '#dc2626', bg: '#fee2e2' };
};

/* ── Row background by dispatch status ── */
const rowBg = (status) => {
  if (status === 'done')    return '#f0fdf4';
  if (status === 'partial') return '#fffbeb';
  return '#fff';
};

/* ── Dispatch Progress pill ── */
const DispatchPill = ({ dispatched, total, status }) => {
  if (status === 'none') return <span style={{ color: '#9ca3af', fontSize: '0.8rem' }}>—</span>;
  if (status === 'done') return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      background: '#d1fae5', color: '#059669',
      borderRadius: 999, padding: '3px 10px',
      fontSize: '0.74rem', fontWeight: 700,
    }}>
      <FiCheckCircle size={11} /> Fully Dispatched
    </span>
  );
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <div style={{ width: 60, height: 5, borderRadius: 99, background: '#e5e7eb', overflow: 'hidden' }}>
        <div style={{
          height: '100%', borderRadius: 99, background: '#f59e0b',
          width: `${total > 0 ? Math.round((dispatched / total) * 100) : 0}%`,
        }} />
      </div>
      <span style={{ fontSize: '0.78rem', fontWeight: 700, color: '#d97706', whiteSpace: 'nowrap' }}>
        {dispatched}/{total} dispatched
      </span>
    </div>
  );
};

/* ── Dispatch Drawer ── */
const DispatchDrawer = ({ order, onClose, onSaved }) => {
  const { log } = useStaffLog();
  const items = safeArray(order.items);
  const [deliveryEntries, setDeliveryEntries] = useState(safeArray(order.deliveryEntries));
  const [inputs, setInputs] = useState({});   // { [lineId]: { qty, date, note } }
  const [saving, setSaving] = useState(false);
  const [bulkDate, setBulkDate] = useState(new Date().toISOString().slice(0, 10));

  /* Payment due check — cash orders where full payment not received */
  const isCash = order.paymentType !== 'credit';
  const grandTotal    = Number(order.grandTotal)     || 0;
  const amountReceived = Number(order.amountReceived) || 0;
  const paymentDue = isCash && grandTotal > 0 && amountReceived < grandTotal - 0.01;

  useEffect(() => {
    const h = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', h);
    return () => document.removeEventListener('keydown', h);
  }, [onClose]);

  /* Pre-fill qty inputs with remaining qty for each line */
  useEffect(() => {
    const init = {};
    items.forEach((line) => {
      const readyQty     = totalQtyForLine(safeArray(order.readyEntries), line.id);
      const dispatched   = totalQtyForLine(deliveryEntries, line.id);
      const remaining    = Math.max(0, readyQty - dispatched);
      init[line.id] = {
        qty:  remaining > 0 ? String(remaining) : '',
        date: bulkDate,
        note: '',
      };
    });
    setInputs(init);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* Sync all per-line dates when bulk date changes */
  const handleBulkDateChange = (val) => {
    setBulkDate(val);
    setInputs((prev) => {
      const updated = { ...prev };
      Object.keys(updated).forEach((lineId) => {
        updated[lineId] = { ...updated[lineId], date: val };
      });
      return updated;
    });
  };

  const setField = (lineId, field, value) =>
    setInputs((prev) => ({ ...prev, [lineId]: { ...prev[lineId], [field]: value } }));

  const handleSave = async () => {
    const newEntries = [];
    let hasAny = false;

    for (const line of items) {
      const inp = inputs[line.id] || {};
      const qty = Number(inp.qty);
      if (!qty || qty <= 0) continue;

      const readyQty   = totalQtyForLine(safeArray(order.readyEntries), line.id);
      const dispatched = totalQtyForLine(deliveryEntries, line.id);
      const remaining  = Math.max(0, readyQty - dispatched);

      if (qty > remaining) {
        toast.error(`${line.product}: qty exceeds remaining (${remaining})`);
        return;
      }
      newEntries.push({
        id: Date.now() + Math.random(),
        itemLineId: line.id,
        qty,
        date: inp.date || new Date().toISOString().slice(0, 10),
        note: inp.note || '',
      });
      hasAny = true;
    }

    if (!hasAny) { toast.error('Enter qty for at least one item'); return; }

    setSaving(true);
    try {
      const updatedEntries = [...deliveryEntries, ...newEntries];
      const { data } = await API.put(`/sales-orders/${order.id}`, {
        deliveryEntries: updatedEntries,
      });
      const updatedOrder = data.order || { ...order, deliveryEntries: updatedEntries };
      setDeliveryEntries(updatedEntries);
      onSaved(updatedOrder);
      toast.success('Dispatch recorded ✓');
      await log('dispatched_order', `Recorded dispatch for order #${order.orderNumber || order.id}`, 'SalesOrder', order.id);
      onClose();
    } catch {
      toast.error('Could not save dispatch');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 9000 }}
      />
      {/* Drawer */}
      <div style={{
        position: 'fixed', top: 0, right: 0, bottom: 0,
        width: 520, maxWidth: '95vw',
        background: '#f9fafb', zIndex: 9001,
        display: 'flex', flexDirection: 'column',
        boxShadow: '-8px 0 40px rgba(0,0,0,0.14)',
        animation: 'slideInDrawer 0.22s ease',
      }}>
        <style>{`@keyframes slideInDrawer { from { transform: translateX(100%); } to { transform: translateX(0); } }`}</style>

        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '16px 20px', background: '#fff', borderBottom: '1px solid #e5e7eb', flexShrink: 0,
        }}>
          <div>
            <div style={{ fontWeight: 800, fontSize: '1rem', color: '#111' }}>{order.orderNumber}</div>
            <div style={{ fontSize: '0.78rem', color: '#9ca3af', marginTop: 2 }}>
              {order.companyName || order.customerName}
              {order.deliveryType && (
                <span style={{
                  marginLeft: 8, fontSize: '0.7rem', fontWeight: 700,
                  background: order.deliveryType === 'delivery' ? '#dbeafe' : '#f3f4f6',
                  color:      order.deliveryType === 'delivery' ? '#2563eb' : '#6b7280',
                  borderRadius: 999, padding: '1px 7px',
                }}>
                  {order.deliveryType === 'delivery' ? 'Courier' : 'Pickup'}
                </span>
              )}
            </div>
          </div>
          <button onClick={onClose} style={{
            border: 'none', background: 'none', cursor: 'pointer',
            color: '#9ca3af', display: 'flex', padding: 4, borderRadius: 6,
          }}>
            <FiX size={18} />
          </button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: 20 }}>

          {/* Payment due warning */}
          {paymentDue && (
            <div style={{
              display: 'flex', alignItems: 'flex-start', gap: 10,
              background: '#fff7ed', border: '1px solid #fed7aa',
              borderRadius: 10, padding: '12px 14px', marginBottom: 14,
            }}>
              <span style={{ fontSize: '1rem', lineHeight: 1 }}>⚠️</span>
              <div>
                <div style={{ fontWeight: 700, fontSize: '0.85rem', color: '#c2410c' }}>
                  Payment Due — ₹{(grandTotal - amountReceived).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} pending
                </div>
                <div style={{ fontSize: '0.78rem', color: '#9a3412', marginTop: 2 }}>
                  This is a cash order. Full payment has not been received before dispatch.
                </div>
              </div>
            </div>
          )}

          {/* Bulk date picker */}
          {items.some((line) => {
            const readyQty  = totalQtyForLine(safeArray(order.readyEntries), line.id);
            const dispatched = totalQtyForLine(deliveryEntries, line.id);
            return Math.max(0, readyQty - dispatched) > 0;
          }) && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 10,
              background: '#eff6ff', border: '1px solid #bfdbfe',
              borderRadius: 10, padding: '10px 14px', marginBottom: 14,
            }}>
              <span style={{ fontSize: '0.8rem', fontWeight: 700, color: '#1d4ed8', whiteSpace: 'nowrap' }}>
                Set date for all:
              </span>
              <input
                type="date"
                value={bulkDate}
                onChange={(e) => handleBulkDateChange(e.target.value)}
                onClick={(e) => e.target.showPicker && e.target.showPicker()}
                style={{
                  padding: '6px 10px', border: '1px solid #bfdbfe',
                  borderRadius: 8, fontSize: 13, outline: 'none',
                  background: '#fff', color: '#1d4ed8', fontWeight: 600, cursor: 'pointer',
                }}
              />
            </div>
          )}

          {/* Dispatch history per item */}
          {items.map((line) => {
            const readyQty    = totalQtyForLine(safeArray(order.readyEntries), line.id);
            const dispatched  = totalQtyForLine(deliveryEntries, line.id);
            const remaining   = Math.max(0, readyQty - dispatched);
            const fullyDone   = remaining === 0 && readyQty > 0;
            const inp         = inputs[line.id] || {};

            const lineEntries = safeArray(deliveryEntries)
              .filter((e) => e.itemLineId === line.id)
              .sort((a, b) => (a.date || '').localeCompare(b.date || ''));

            return (
              <div key={line.id} style={{
                background: '#fff', border: '1px solid #e5e7eb',
                borderRadius: 12, marginBottom: 14, overflow: 'hidden',
              }}>
                {/* Item header */}
                <div style={{
                  padding: '12px 16px', borderBottom: '1px solid #f3f4f6',
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                }}>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: '0.9rem', color: '#111' }}>
                      {line.product || '(unnamed)'}
                    </div>
                    <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: 2 }}>
                      Ordered: {line.qty} {line.unit} · Ready: {readyQty} {line.unit}
                    </div>
                  </div>
                  <span style={{
                    fontSize: '0.74rem', fontWeight: 700, borderRadius: 999, padding: '3px 10px',
                    background: fullyDone ? '#d1fae5' : dispatched > 0 ? '#fef3c7' : '#f3f4f6',
                    color:      fullyDone ? '#059669' : dispatched > 0 ? '#d97706' : '#6b7280',
                  }}>
                    {fullyDone
                      ? `All ${readyQty} dispatched ✓`
                      : `${dispatched}/${readyQty} dispatched`}
                  </span>
                </div>

                {/* Past dispatch entries */}
                {lineEntries.length > 0 && (
                  <div style={{ padding: '8px 16px', borderBottom: '1px solid #f3f4f6' }}>
                    <div style={{ fontSize: '0.7rem', fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 6 }}>
                      Dispatch History
                    </div>
                    {lineEntries.map((entry, idx) => (
                      <div key={entry.id} style={{
                        display: 'flex', alignItems: 'center', gap: 10,
                        fontSize: '0.8rem', color: '#374151', marginBottom: 4,
                      }}>
                        <span style={{ color: '#9ca3af', fontWeight: 600, minWidth: 16 }}>{idx + 1}.</span>
                        <span style={{ fontWeight: 700 }}>{entry.qty} {line.unit}</span>
                        <span style={{ color: '#9ca3af' }}>·</span>
                        <span style={{ color: '#6b7280' }}>{fmtDate(entry.date)}</span>
                        {entry.note && (
                          <>
                            <span style={{ color: '#9ca3af' }}>·</span>
                            <span style={{ color: '#6b7280', fontStyle: 'italic' }}>{entry.note}</span>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* Input form — only if remaining > 0 */}
                {!fullyDone && remaining > 0 && (
                  <div style={{ padding: '12px 16px', background: '#fafafa' }}>
                    <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#374151', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                      Record Dispatch — {remaining} {line.unit} remaining
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '80px 1fr 1fr', gap: 8 }}>
                      <div>
                        <label style={{ fontSize: '0.7rem', fontWeight: 600, color: '#6b7280', display: 'block', marginBottom: 3 }}>Qty *</label>
                        <input
                          type="number" min={1} max={remaining}
                          value={inp.qty || ''}
                          onChange={(e) => setField(line.id, 'qty', e.target.value)}
                          style={{
                            width: '100%', boxSizing: 'border-box',
                            padding: '7px 8px', border: '1px solid #d1d5db',
                            borderRadius: 8, fontSize: 13, outline: 'none',
                          }}
                        />
                      </div>
                      <div>
                        <label style={{ fontSize: '0.7rem', fontWeight: 600, color: '#6b7280', display: 'block', marginBottom: 3 }}>Date *</label>
                        <input
                          type="date"
                          value={inp.date || ''}
                          onChange={(e) => setField(line.id, 'date', e.target.value)}
                          onClick={(e) => e.target.showPicker && e.target.showPicker()}
                          style={{
                            width: '100%', boxSizing: 'border-box',
                            padding: '7px 8px', border: '1px solid #d1d5db',
                            borderRadius: 8, fontSize: 13, outline: 'none', cursor: 'pointer',
                          }}
                        />
                      </div>
                      <div>
                        <label style={{ fontSize: '0.7rem', fontWeight: 600, color: '#6b7280', display: 'block', marginBottom: 3 }}>Note / Courier</label>
                        <input
                          type="text"
                          value={inp.note || ''}
                          onChange={(e) => setField(line.id, 'note', e.target.value)}
                          placeholder="e.g. Delhivery #123"
                          style={{
                            width: '100%', boxSizing: 'border-box',
                            padding: '7px 8px', border: '1px solid #d1d5db',
                            borderRadius: 8, fontSize: 13, outline: 'none',
                          }}
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div style={{
          padding: '14px 20px', background: '#fff',
          borderTop: '1px solid #e5e7eb', flexShrink: 0,
          display: 'flex', gap: 10, justifyContent: 'flex-end',
        }}>
          <button onClick={onClose} style={{
            padding: '9px 18px', borderRadius: 9, border: '1px solid #e5e7eb',
            background: '#f9fafb', color: '#6b7280', fontWeight: 600, fontSize: '0.88rem', cursor: 'pointer',
          }}>
            Cancel
          </button>
          <button onClick={handleSave} disabled={saving} style={{
            padding: '9px 20px', borderRadius: 9, border: 'none',
            background: saving ? '#9ca3af' : '#2563eb', color: '#fff',
            fontWeight: 700, fontSize: '0.88rem', cursor: saving ? 'default' : 'pointer',
          }}>
            {saving ? 'Saving…' : 'Save Dispatch ✓'}
          </button>
        </div>
      </div>
    </>
  );
};

const PAGE_SIZE = 25;

/* ── Main component ── */
const AdminDelivered = () => {
  const [orders, setOrders]       = useState([]);
  const [loading, setLoading]     = useState(true);
  const [search, setSearch]       = useState('');
  const [dateFrom, setDateFrom]   = useState('');
  const [dateTo, setDateTo]       = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [drawer, setDrawer]       = useState(null);
  const [page, setPage]           = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  const load = useCallback(async ({
    targetPage = 1,
    q          = search,
    dFrom      = dateFrom,
    dTo        = dateTo,
    fStatus    = filterStatus,
  } = {}) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q.trim()) params.set('q', q.trim());
      params.set('page', targetPage);
      params.set('limit', PAGE_SIZE);
      // Only show confirmed + delivered orders that have readyEntries (handled server-side via dispatchStatus param)
      // Sending dispatchStatus always (even 'all') tells the backend to apply the readyEntries filter
      params.set('dispatchStatus', fStatus === 'all' ? 'all' : fStatus);
      if (dFrom) params.set('readyFrom', dFrom);
      if (dTo)   params.set('readyTo',   dTo);
      const { data } = await API.get(`/sales-orders?${params}`);
      const orders = data.orders || [];
      // Sort: undispatched/partial first, done last
      orders.sort((a, b) => {
        const isDoneA = getDispatchProgress(a).status === 'done';
        const isDoneB = getDispatchProgress(b).status === 'done';
        if (isDoneA !== isDoneB) return isDoneA ? 1 : -1;
        return b.id - a.id;
      });
      setOrders(orders);
      setTotalPages(data.pages || 1);
      setTotalCount(data.count || 0);
      setPage(targetPage);
    } catch {
      toast.error('Could not load orders');
    } finally {
      setLoading(false);
    }
  }, [search, dateFrom, dateTo, filterStatus]);

  useEffect(() => { load(); }, [load]);

  // Search — debounced, reset to page 1
  useEffect(() => {
    const t = setTimeout(() => load({ targetPage: 1, q: search }), 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  // Date and status filters — immediate, reset to page 1
  useEffect(() => {
    load({ targetPage: 1, dFrom: dateFrom, dTo: dateTo, fStatus: filterStatus });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateFrom, dateTo, filterStatus]);

  const handleOrderSaved = useCallback((updated) => {
    setOrders((prev) => prev.map((o) => o.id === updated.id ? { ...o, ...updated } : o));
  }, []);

  const openDrawer = useCallback(async (order) => {
    setDrawer(order);
    try {
      const { data } = await API.get(`/sales-orders/${order.id}`);
      const fresh = data.order || data;
      if (fresh) setDrawer(fresh);
    } catch {
      // keep cached version
    }
  }, []);

  const toISO = (d) => { if (!d) return ''; return new Date(d).toISOString().slice(0, 10); };

  const hasFilter = dateFrom || dateTo || filterStatus !== 'all';

  // Summary counts
  const pendingCount = orders.filter((o) => getDispatchProgress(o).status === 'pending').length;
  const partialCount = orders.filter((o) => getDispatchProgress(o).status === 'partial').length;
  const doneCount    = orders.filter((o) => getDispatchProgress(o).status === 'done').length;

  return (
    <div className="so-page">

      {/* Header */}
      <div className="so-page-head">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <FiTruck size={20} color="#2563eb" />
          <div>
            <h2 style={{ margin: 0 }}>Delivery</h2>
          </div>
        </div>
        {/* Summary chips */}
        <div style={{ display: 'flex', gap: 8, marginLeft: 'auto', flexWrap: 'wrap' }}>
          {pendingCount > 0 && (
            <span style={{ fontSize: '0.78rem', fontWeight: 700, background: '#f3f4f6', color: '#6b7280', borderRadius: 999, padding: '4px 12px' }}>
              {pendingCount} pending
            </span>
          )}
          {partialCount > 0 && (
            <span style={{ fontSize: '0.78rem', fontWeight: 700, background: '#fef3c7', color: '#d97706', borderRadius: 999, padding: '4px 12px' }}>
              {partialCount} partial
            </span>
          )}
          {doneCount > 0 && (
            <span style={{ fontSize: '0.78rem', fontWeight: 700, background: '#d1fae5', color: '#059669', borderRadius: 999, padding: '4px 12px' }}>
              {doneCount} done
            </span>
          )}
        </div>
      </div>

      {/* Filter bar */}
      <div className="so-filter-bar">
        <div className="so-search-input so-search-input--filter">
          <FiSearch size={15} />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search order, customer, phone…"
          />
          <button onClick={load} title="Refresh"
            style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#9ca3af', display: 'flex', padding: 2 }}>
            <FiRefreshCw size={14} />
          </button>
        </div>

        {/* Ready date range */}
        <div className="so-filter-dates">
          <span className="so-date-label">Ready</span>
          <input type="date" className="so-date-input" title="Ready from"
            value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
            onClick={(e) => e.target.showPicker && e.target.showPicker()} />
          <span className="so-date-sep">–</span>
          <input type="date" className="so-date-input" title="Ready to"
            value={dateTo} onChange={(e) => setDateTo(e.target.value)}
            onClick={(e) => e.target.showPicker && e.target.showPicker()} />
        </div>

        {/* Status pills */}
        <div className="so-pill-group">
          {[
            ['all',     'All'],
            ['pending', 'Pending'],
            ['partial', 'Partial'],
            ['done',    'Done'],
          ].map(([val, label]) => (
            <button key={val}
              className={`so-pill${filterStatus === val ? ' so-pill--active' : ''}`}
              onClick={() => setFilterStatus(val)}>
              {label}
            </button>
          ))}
        </div>

        {hasFilter && (
          <button className="so-clear-btn" onClick={() => {
            setDateFrom(''); setDateTo(''); setFilterStatus('all');
          }}>Clear</button>
        )}
      </div>

      {/* Table */}
      <div className="so-card so-card--table">
        {loading ? (
          <div className="so-empty">Loading…</div>
        ) : orders.length === 0 ? (
          <div className="so-empty">
            {search || hasFilter ? 'No matching orders.' : 'No orders ready for dispatch yet.'}
          </div>
        ) : (
          <div className="so-table-scroll">
            <table className="so-table">
              <thead>
                <tr>
                  <th>Order #</th>
                  <th>Customer</th>
                  <th>Delivery Type</th>
                  <th>Delivery Date</th>
                  <th>Dispatch Progress</th>
                  <th>Payment Type</th>
                  <th>Payment</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {orders.map((order) => {
                  const prog   = getDispatchProgress(order);
                  const ps     = paymentStatus(order);
                  const fullyDone = prog.status === 'done';

                  return (
                    <tr
                      key={order.id}
                      style={{ background: rowBg(prog.status) }}
                      onClick={() => openDrawer(order)}
                    >
                      <td className="so-strong" style={{ color: 'var(--primary)', whiteSpace: 'nowrap' }}>
                        {order.orderNumber}
                      </td>
                      <td>
                        <div className="so-strong">{order.companyName || order.customerName}</div>
                        {order.companyName && <div className="so-muted">{order.customerName}</div>}
                        <div className="so-muted">{order.customerPhone}</div>
                      </td>
                      <td>
                        <span style={{
                          fontSize: '0.74rem', fontWeight: 700, borderRadius: 999, padding: '3px 10px',
                          background: order.deliveryType === 'delivery' ? '#dbeafe' : '#f3f4f6',
                          color:      order.deliveryType === 'delivery' ? '#2563eb' : '#6b7280',
                        }}>
                          {order.deliveryType === 'delivery' ? 'Courier' : 'Pickup'}
                        </span>
                      </td>
                      <td className="so-muted" style={{ whiteSpace: 'nowrap' }}>
                        {order.deliveryDate ? fmtDate(order.deliveryDate) : '—'}
                      </td>
                      <td>
                        <DispatchPill
                          dispatched={prog.dispatched}
                          total={prog.total}
                          status={prog.status}
                        />
                      </td>
                      <td>
                        <span style={{
                          fontSize: '0.74rem', fontWeight: 700, borderRadius: 999, padding: '3px 10px',
                          whiteSpace: 'nowrap',
                          background: order.paymentType === 'credit' ? '#d1fae5' : '#f3f4f6',
                          color:      order.paymentType === 'credit' ? '#059669' : '#6b7280',
                        }}>
                          {order.paymentType === 'credit' ? 'Credit' : 'Cash'}
                        </span>
                      </td>
                      <td>
                        <span className="so-badge" style={{ background: ps.bg, color: ps.color, whiteSpace: 'nowrap' }}>
                          {ps.label}
                        </span>
                      </td>
                      <td onClick={(e) => e.stopPropagation()}>
                        {!fullyDone ? (
                          <button
                            onClick={() => openDrawer(order)}
                            style={{
                              padding: '6px 14px', borderRadius: 8, border: 'none',
                              background: '#2563eb', color: '#fff',
                              fontWeight: 700, fontSize: '0.78rem', cursor: 'pointer',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            Dispatch
                          </button>
                        ) : (
                          <span style={{ color: '#059669', fontSize: '0.78rem', fontWeight: 700 }}>✓ Done</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {!loading && orders.length > 0 && (
          <div style={{
            padding: '8px 14px', borderTop: '1px solid #f3f4f6',
            fontSize: '0.7rem', color: '#b0b7c3', background: '#fafafa',
          }}>
            {orders.length} order{orders.length !== 1 ? 's' : ''} on this page · {totalCount} total
          </div>
        )}
      </div>

      {/* Pagination */}
      {!loading && totalPages > 1 && (
        <div style={{
          display: 'flex', justifyContent: 'space-between',
          alignItems: 'center', marginTop: 16, flexWrap: 'wrap', gap: 8,
        }}>
          <span style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>
            Page {page} of {totalPages} &nbsp;·&nbsp; {totalCount} orders
          </span>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              className="so-btn"
              onClick={() => load({ targetPage: page - 1 })}
              disabled={page <= 1}
              style={{ display: 'flex', alignItems: 'center', gap: 4, opacity: page <= 1 ? 0.4 : 1 }}
            >
              <FiChevronLeft size={15} /> Prev
            </button>
            <button
              className="so-btn"
              onClick={() => load({ targetPage: page + 1 })}
              disabled={page >= totalPages}
              style={{ display: 'flex', alignItems: 'center', gap: 4, opacity: page >= totalPages ? 0.4 : 1 }}
            >
              Next <FiChevronRight size={15} />
            </button>
          </div>
        </div>
      )}

      {/* Dispatch Drawer */}
      {drawer && (
        <DispatchDrawer
          order={drawer}
          onClose={() => setDrawer(null)}
          onSaved={handleOrderSaved}
        />
      )}
    </div>
  );
};

export default AdminDelivered;